/**
 * Outcomes view — Rate Card tab, plan §11 item 3 ("Outcomes: one row per
 * negotiation with the metric set (§10.2), sort and filter by sponsor,
 * indication, therapeutic area, CRO, year and recruitment difficulty, group
 * means and medians, and fees requested versus received (§10.4)."); §13.8
 * WP-72.
 *
 * THE PANEL IS NOT REACHABLE IN THE APP UNTIL THE MOUNT STEP
 * (metrics.mount.md): RateCardTab.tsx's Outcomes branch is still WP-41's
 * EmptyState placeholder — this WP does not touch that file. Until the mount
 * step pastes the one-line import and swaps that block, this component is
 * reachable only through frontend/src/contracts/__tests__/outcomes.test.tsx.
 *
 * THE FEES SLOT (`feesPanel` prop) IS THE WHOLE POINT OF THIS SPLIT: WP-71
 * owns frontend/src/contracts/tabs/rateCard/outcomes/fees/FeesPanel.tsx and
 * built it WHILE THIS WP RAN, concurrently — this file never imports, lazy
 * imports, or jest.mocks that path, or any path under outcomes/fees/. When
 * `feesPanel` is omitted this view renders a labelled "pending WP-71" slot
 * (§10.4: fees requested versus received); the mount step passes a real
 * `<FeesPanel />` element once WP-71 has landed.
 *
 * Data comes from ../../../api/metrics (a NEW module, api/rateCard.ts's
 * style — never axios directly under frontend/src/contracts). Built out of
 * the shared frontend/src/ui primitives per the binding design skill
 * (.claude/skills/siteoperations-design/SKILL.md): SearchInput, FilterBar,
 * FilterSelect, ActiveFilters, SegmentedControl, EmptyState, plus the
 * styles.ts recipes for the table and the status pill. PageHeader belongs
 * to the tab (RateCardTab.tsx), not to this view.
 *
 * NAME RESOLUTION NOTE: §10.2's own data model gives a row only
 * sponsor_id/cro_id/therapeutic_area_id/indication_id (uuids) — resolving
 * those to master-list names is a master-list read this WP does not own
 * (services/contracts/masterLists is consume-only territory elsewhere in
 * this module, and no such join is part of listMetrics's documented
 * response). Filter options and the row cells below therefore show the raw
 * id. A later WP wiring this view into a name-resolving context (the
 * master-list cache the Rate Card Card view already has) can swap the
 * display without touching this component's data flow.
 */
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { AlertTriangle, BarChart3, Layers } from 'lucide-react';
// Every ui/ primitive below is imported from ITS OWN file, never the
// `../../../../ui` barrel: the barrel's index.ts also re-exports
// StudyPicker/StudyDocumentsCard, which import services/api (axios) —
// pulling that in would break this module's own test (this repo's jest
// cannot parse axios, hazard list) without a services/api mock this view
// has no other reason to carry. Same reasoning as FeesPanel.tsx's own
// EmptyState-by-file import (WP-71's header comment).
import SearchInput from '../../../../ui/SearchInput';
import FilterBar from '../../../../ui/FilterBar';
import FilterSelect from '../../../../ui/FilterSelect';
import ActiveFilters, { type ActiveChip } from '../../../../ui/ActiveFilters';
import SegmentedControl from '../../../../ui/SegmentedControl';
import EmptyState from '../../../../ui/EmptyState';
import {
  getMetrics,
  type ListMetricsResult,
  type MetricsListRow,
  type MetricsGroup,
  type MetricsFilter,
  type MetricsGroupBy,
  type MetricsSort,
  type MetricValue,
} from '../../../api/metrics';

export interface OutcomesViewProps {
  /** WP-71's FeesPanel element, wired by the MOUNT step; omitted renders the pending slot (§10.4). */
  feesPanel?: ReactNode;
}

// ---------------------------------------------------------------------------
// Column set — a deliberately readable subset of §10.2's full key list; every
// OTHER key is still fetched and still shown on hover (title attribute), it
// just is not its own column (a §10.2-key-per-column table would run to
// dozens of columns and defeat "readable at a glance").
// ---------------------------------------------------------------------------

interface MetricColumn {
  key: string;
  label: string;
  format: 'money' | 'pct' | 'points' | 'int';
}

const METRIC_COLUMNS: MetricColumn[] = [
  { key: 'cpp_offered', label: 'CPP offered', format: 'money' },
  { key: 'cpp_final', label: 'CPP final', format: 'money' },
  { key: 'cpp_increase_pct', label: 'CPP +/-', format: 'pct' },
  { key: 'fees_offered', label: 'Fees offered', format: 'money' },
  { key: 'fees_final', label: 'Fees final', format: 'money' },
  { key: 'total_offered', label: 'Total offered', format: 'money' },
  { key: 'total_final', label: 'Total final', format: 'money' },
  { key: 'overhead_offered', label: 'OH offered', format: 'pct' },
  { key: 'overhead_final', label: 'OH final', format: 'pct' },
  { key: 'holdback_offered', label: 'Holdback', format: 'pct' },
];

const ROW_SORT_COLUMNS: { key: string; label: string }[] = [
  { key: 'protocol_number', label: 'Protocol' },
  { key: 'year', label: 'Year' },
  { key: 'recruitment_difficulty', label: 'Difficulty' },
];

const GROUP_BY_OPTIONS: { value: MetricsGroupBy | 'none'; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'sponsor', label: 'Sponsor' },
  { value: 'indication', label: 'Indication' },
  { value: 'therapeutic_area', label: 'Therapeutic area' },
  { value: 'cro', label: 'CRO' },
  { value: 'year', label: 'Year' },
  { value: 'recruitment_difficulty', label: 'Difficulty' },
];

// ---------------------------------------------------------------------------
// Formatting — never a fabricated 0 for null (G13)
// ---------------------------------------------------------------------------

const moneyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

function formatValue(value: number | null, format: MetricColumn['format']): string {
  if (value === null || value === undefined) return '—';
  if (format === 'money') return moneyFormatter.format(value);
  if (format === 'pct') return `${(value * 100).toFixed(1)}%`;
  if (format === 'points') return `${Math.round(value * 100)} points`;
  return String(value);
}

function titleFor(metric: MetricValue | undefined): string {
  if (!metric) return 'no data';
  const parts = [`label: ${metric.label}`];
  if (metric.formula) parts.push(`formula: ${metric.formula}`);
  const versionIds = metric.provenance?.version_ids ?? [];
  if (versionIds.length > 0) parts.push(`versions: ${versionIds.join(', ')}`);
  const lineIds = metric.provenance?.line_ids ?? [];
  if (lineIds.length > 0) parts.push(`lines: ${lineIds.length}`);
  // WP-87, ledger item 122: cpp_printed_* names the cell it was read from.
  const sheet = metric.provenance?.sheet;
  const cell = metric.provenance?.cell;
  if (sheet && cell) parts.push(`cell: ${sheet}!${cell}`);
  return parts.join(' • ');
}

function distinctOptions(rows: MetricsListRow[], field: keyof MetricsListRow): string[] {
  const set = new Set<string>();
  for (const row of rows) {
    const value = row[field];
    if (typeof value === 'string' && value.length > 0) set.add(value);
  }
  return [...set].sort();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OutcomesView({ feesPanel }: OutcomesViewProps) {
  const [result, setResult] = useState<ListMetricsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<MetricsFilter>({});
  const [sort, setSort] = useState<MetricsSort | null>(null);
  const [groupBy, setGroupBy] = useState<MetricsGroupBy | 'none'>('none');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getMetrics({
      sort: sort ?? undefined,
      filter,
      groupBy: groupBy === 'none' ? undefined : groupBy,
    })
      .then((data) => {
        if (!cancelled) setResult(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load metrics');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filter/sort/groupBy are plain objects rebuilt intentionally on every change we care about
  }, [JSON.stringify(filter), sort?.key, sort?.direction, groupBy]);

  const rows = result?.rows ?? [];
  const groups = result?.groups ?? null;

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (term.length === 0) return rows;
    return rows.filter((row) => {
      const haystack = [row.protocol_number, row.sponsor_id, row.cro_id, row.indication_id, row.therapeutic_area_id]
        .filter((v): v is string => typeof v === 'string')
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [rows, search]);

  const sponsorOptions = useMemo(() => distinctOptions(rows, 'sponsor_id'), [rows]);
  const indicationOptions = useMemo(() => distinctOptions(rows, 'indication_id'), [rows]);
  const areaOptions = useMemo(() => distinctOptions(rows, 'therapeutic_area_id'), [rows]);
  const croOptions = useMemo(() => distinctOptions(rows, 'cro_id'), [rows]);
  const yearOptions = useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) set.add(String(row.year));
    return [...set].sort();
  }, [rows]);

  function setFilterField<K extends keyof MetricsFilter>(key: K, value: MetricsFilter[K] | '') {
    setFilter((prev) => {
      const next = { ...prev };
      if (value === '' || value === undefined) delete next[key];
      else next[key] = value;
      return next;
    });
  }

  function toggleSort(key: string) {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  }

  const activeChips: ActiveChip[] = [];
  if (filter.sponsorId) activeChips.push({ key: 'sponsor', label: `Sponsor: ${filter.sponsorId}`, onRemove: () => setFilterField('sponsorId', '') });
  if (filter.indicationId) activeChips.push({ key: 'indication', label: `Indication: ${filter.indicationId}`, onRemove: () => setFilterField('indicationId', '') });
  if (filter.therapeuticAreaId) activeChips.push({ key: 'area', label: `Area: ${filter.therapeuticAreaId}`, onRemove: () => setFilterField('therapeuticAreaId', '') });
  if (filter.croId) activeChips.push({ key: 'cro', label: `CRO: ${filter.croId}`, onRemove: () => setFilterField('croId', '') });
  if (filter.year !== undefined) activeChips.push({ key: 'year', label: `Year: ${filter.year}`, onRemove: () => setFilterField('year', undefined) });
  if (filter.recruitmentDifficulty !== undefined) {
    activeChips.push({
      key: 'difficulty',
      label: `Difficulty: ${filter.recruitmentDifficulty}`,
      onRemove: () => setFilterField('recruitmentDifficulty', undefined),
    });
  }

  return (
    <div className="space-y-4">
      <FilterBar
        active={<ActiveFilters chips={activeChips} onClearAll={() => setFilter({})} />}
      >
        <div data-testid="outcomes-search">
          <SearchInput value={search} onChange={setSearch} placeholder="Search protocol, sponsor, CRO..." className="basis-64" />
        </div>
        <div data-testid="outcomes-filter-sponsor">
          <FilterSelect label="Sponsor" value={filter.sponsorId ?? ''} onChange={(v) => setFilterField('sponsorId', v)} auto>
            <option value="">All sponsors</option>
            {sponsorOptions.map((id) => (
              <option key={id} value={id}>{id}</option>
            ))}
          </FilterSelect>
        </div>
        <div data-testid="outcomes-filter-indication">
          <FilterSelect label="Indication" value={filter.indicationId ?? ''} onChange={(v) => setFilterField('indicationId', v)} auto>
            <option value="">All indications</option>
            {indicationOptions.map((id) => (
              <option key={id} value={id}>{id}</option>
            ))}
          </FilterSelect>
        </div>
        <div data-testid="outcomes-filter-area">
          <FilterSelect label="Therapeutic area" value={filter.therapeuticAreaId ?? ''} onChange={(v) => setFilterField('therapeuticAreaId', v)} auto>
            <option value="">All areas</option>
            {areaOptions.map((id) => (
              <option key={id} value={id}>{id}</option>
            ))}
          </FilterSelect>
        </div>
        <div data-testid="outcomes-filter-cro">
          <FilterSelect label="CRO" value={filter.croId ?? ''} onChange={(v) => setFilterField('croId', v)} auto>
            <option value="">All CROs</option>
            {croOptions.map((id) => (
              <option key={id} value={id}>{id}</option>
            ))}
          </FilterSelect>
        </div>
        <div data-testid="outcomes-filter-year">
          <FilterSelect
            label="Year"
            value={filter.year !== undefined ? String(filter.year) : ''}
            onChange={(v) => setFilterField('year', v === '' ? undefined : (Number.isNaN(Number(v)) ? (v as 'unknown') : Number(v)))}
            auto
          >
            <option value="">All years</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </FilterSelect>
        </div>
        <div data-testid="outcomes-filter-difficulty">
          <FilterSelect
            label="Recruitment difficulty"
            value={filter.recruitmentDifficulty !== undefined ? String(filter.recruitmentDifficulty) : ''}
            onChange={(v) => setFilterField('recruitmentDifficulty', v === '' ? undefined : Number(v))}
            auto
          >
            <option value="">Any difficulty</option>
            {[1, 2, 3, 4, 5].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </FilterSelect>
        </div>
      </FilterBar>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-widest text-gray-400 dark:text-gray-500">Group by</span>
          <SegmentedControl
            label="Group by"
            value={groupBy}
            onChange={(v) => setGroupBy(v as MetricsGroupBy | 'none')}
            options={GROUP_BY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          />
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {!loading && !error && visibleRows.length === 0 ? (
        <EmptyState
          icon={BarChart3}
          title="No negotiations to show"
          hint="Once budgets are ingested and their offered and final versions are set, their outcomes appear here (§10.2)."
          variant="card"
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-testid="outcomes-table">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                {ROW_SORT_COLUMNS.map((col) => (
                  <SortableHeader key={col.key} column={col} sort={sort} onToggle={toggleSort} />
                ))}
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Sponsor</th>
                {METRIC_COLUMNS.map((col) => (
                  <SortableHeader key={col.key} column={col} sort={sort} onToggle={toggleSort} />
                ))}
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {visibleRows.map((row) => (
                <OutcomeRow key={row.negotiation_id} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {groupBy !== 'none' && groups && groups.length > 0 && (
        <GroupsPanel groups={groups} groupBy={groupBy} />
      )}

      <FeesSlot feesPanel={feesPanel} />
    </div>
  );
}

function SortableHeader({
  column,
  sort,
  onToggle,
}: {
  column: { key: string; label: string };
  sort: MetricsSort | null;
  onToggle: (key: string) => void;
}) {
  const active = sort?.key === column.key;
  const arrow = active ? (sort?.direction === 'asc' ? '↑' : '↓') : '';
  return (
    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
      <button
        type="button"
        onClick={() => onToggle(column.key)}
        className="inline-flex items-center gap-1 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:text-gray-200"
        data-testid={`outcomes-sort-${column.key}`}
      >
        {column.label}
        {arrow && <span aria-hidden="true">{arrow}</span>}
      </button>
    </th>
  );
}

function OutcomeRow({ row }: { row: MetricsListRow }) {
  return (
    <tr className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50" data-testid={`outcomes-row-${row.negotiation_id}`}>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{row.protocol_number ?? '—'}</td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{row.year}</td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{row.recruitment_difficulty ?? '—'}</td>
      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{row.sponsor_id ?? '—'}</td>
      {METRIC_COLUMNS.map((col) => {
        const metric = row.metrics[col.key];
        return (
          <td
            key={col.key}
            className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100"
            title={titleFor(metric)}
            data-testid={`outcomes-value-${row.negotiation_id}-${col.key}`}
          >
            {formatValue(metric ? metric.final_value : null, col.format)}
          </td>
        );
      })}
      <td className="px-4 py-3 text-sm">
        {row.complete ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/40 dark:text-green-300">
            Complete
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
            title={row.incomplete_reason ?? undefined}
            data-testid={`outcomes-incomplete-${row.negotiation_id}`}
          >
            Incomplete
          </span>
        )}
      </td>
    </tr>
  );
}

function GroupsPanel({ groups, groupBy }: { groups: MetricsGroup[]; groupBy: MetricsGroupBy }) {
  return (
    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700" data-testid="outcomes-groups">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
        <Layers className="h-4 w-4 text-primary-600 dark:text-primary-400" />
        Grouped by {groupBy.replace('_', ' ')}
      </h3>
      <div className="space-y-3">
        {groups.map((group) => (
          <div key={group.key} className="rounded-lg border border-gray-100 p-3 dark:border-gray-700" data-testid={`outcomes-group-${group.key}`}>
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              {group.label}
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400">(n={group.n})</span>
            </div>
            <div className="flex flex-wrap gap-4 text-xs text-gray-600 dark:text-gray-300">
              {METRIC_COLUMNS.map((col) => {
                const stat = group.metrics[col.key];
                if (!stat) return null;
                return (
                  <div key={col.key} data-testid={`outcomes-group-${group.key}-${col.key}`}>
                    <div className="font-medium text-gray-500 dark:text-gray-400">{col.label}</div>
                    <div>
                      mean {formatValue(stat.mean_final, col.format)} · median {formatValue(stat.median_final, col.format)}{' '}
                      <span className="text-gray-400">(n={stat.n})</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FeesSlot({ feesPanel }: { feesPanel?: ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700" data-testid="outcomes-fees-slot">
      <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Fees requested versus received (§10.4)</h3>
      {feesPanel ?? (
        <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="outcomes-fees-pending">
          Fees: pending WP-71
        </p>
      )}
    </div>
  );
}

export default OutcomesView;
